import React, { Suspense, lazy, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Link,
  Menu,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { FiCalendar, FiEye, FiEyeOff } from 'react-icons/fi';
import { LuChevronDown } from 'react-icons/lu';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useSearchParams } from 'react-router-dom';

import { FinancialStatementTable } from '../../components/FinancialStatements';
import { useColorModeValue } from '../../components/ui/color-mode';
import { normaliseFilingsPayload } from '../../hooks/useSecFilingsData';
import { getStockPriceHistory } from '../../api';
import { getLoginRedirectOptions } from '../../utils/auth0Errors';
import {
  DEFAULT_SEC_FILING_SORT,
  FilingsTable,
  SEC_FILING_COLUMNS,
  SEC_FILING_FILTER_OPTIONS,
  getSecFilingFilterOption,
} from './StockSecFilings';
import StockPriceHistoryChart from './StockPriceHistoryChart';

const SnapshotFinancialRechartsChart = lazy(() =>
  import('recharts').then(({
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
  }) => ({
    default: function SnapshotFinancialRechartsChart({
      chartData,
      metricKeys,
      legendPayload,
      gridStroke,
      tooltipRenderer,
    }) {
      return (
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={chartData} chartKind="financial" margin={{ top: 10, right: 20, left: 10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey="date"
              tickFormatter={formatFiscalYear}
              tick={{ fontSize: 12 }}
              minTickGap={20}
            />
            <YAxis
              tickFormatter={formatChartCurrency}
              tick={{ fontSize: 12 }}
              width={82}
              domain={['auto', 'auto']}
            />
            <Tooltip content={tooltipRenderer} />
            <Legend
              verticalAlign="bottom"
              align="center"
              layout="horizontal"
              wrapperStyle={{ paddingTop: 16 }}
              payload={legendPayload}
            />
            {metricKeys.map((metricKey) => {
              const config = snapshotChartMetricConfig[metricKey] || {};
              return (
                <Line
                  key={metricKey}
                  type="monotone"
                  dataKey={metricKey}
                  name={config.label || metricKey}
                  stroke={config.color || '#8884d8'}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      );
    },
  })),
);

const SnapshotReverseDcfRechartsChart = lazy(() =>
  import('recharts').then(({
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ReferenceLine,
  }) => ({
    default: function SnapshotReverseDcfRechartsChart({
      chartData,
      gridStroke,
      tooltipRenderer,
    }) {
      return (
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={chartData} chartKind="reverse-dcf" margin={{ top: 18, right: 20, left: 10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} minTickGap={16} />
            <YAxis
              tickFormatter={formatChartCurrency}
              tick={{ fontSize: 12 }}
              width={82}
              domain={['auto', 'auto']}
            />
            <Tooltip content={tooltipRenderer} />
            <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: 16 }} />
            <ReferenceLine
              x="Snapshot"
              stroke="#E53E3E"
              strokeDasharray="4 4"
              label={{ value: 'Snapshot', position: 'top', fontSize: 11, fill: '#E53E3E' }}
            />
            <Line
              type="monotone"
              dataKey="historicalFcf"
              name="Historical FCF"
              stroke="#5E6472"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="impliedFcf"
              name="Market-implied FCF"
              stroke="#3182CE"
              strokeWidth={2.5}
              dot={{ r: 3 }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="pvFcf"
              name="PV of implied FCF"
              stroke="#A0AEC0"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              dot={{ r: 2 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      );
    },
  })),
);

const todayIsoDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const chartApiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
const redactedCompanyName = 'Company X';
const redactedTicker = 'XXXX';
const emptyPriceHistoryState = {
  status: 'idle',
  prices: [],
  error: '',
  priceStatus: null,
  priceMessage: null,
  startDate: null,
  endDate: null,
};
const emptyCashFlowOutcomeState = {
  status: 'idle',
  data: null,
  error: '',
};
const emptySnapshotOutcomeState = {
  status: 'idle',
  data: null,
  error: '',
};
const defaultSnapshotPriceHistoryRange = '1y';
const initialStockSnapshotLoadState = {
  status: 'idle',
  error: '',
  snapshotData: null,
  snapshotRequestKey: '',
  outcomesRevealed: false,
  outcomeState: emptySnapshotOutcomeState,
  outcomePriceHistoryState: emptyPriceHistoryState,
  outcomeCashFlowState: emptyCashFlowOutcomeState,
};
const initialStockSnapshotState = {
  ...initialStockSnapshotLoadState,
  snapshotDate: '',
  redactionEnabled: false,
  priceHistoryState: emptyPriceHistoryState,
  snapshotPriceHistoryRange: defaultSnapshotPriceHistoryRange,
};
const emptySnapshotFinancialChartState = {
  status: 'idle',
  chartData: [],
  metrics: [],
  error: '',
};
const emptySnapshotReverseDcfState = {
  status: 'idle',
  chartData: [],
  tableRows: [],
  result: null,
  error: '',
};
const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;
const minimumTypedSnapshotYear = 1000;
const sectionHeadingProps = {
  size: 'sm',
  lineHeight: { base: '1.12', md: '1.2' },
};
const subsectionHeadingProps = {
  size: 'xs',
  lineHeight: { base: '1.12', md: '1.2' },
};
const ratioNumberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});
const percentNumberFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});
const standardNumberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});
const currencyNumberFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
const compactCurrencyNumberFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 2,
});

function stockSnapshotReducer(state, action) {
  switch (action.type) {
    case 'snapshotLoading':
      return {
        ...state,
        ...initialStockSnapshotLoadState,
        status: 'loading',
        snapshotRequestKey: action.requestKey,
      };
    case 'snapshotSuccess':
      if (action.requestKey && state.snapshotRequestKey !== action.requestKey) {
        return state;
      }

      return {
        ...state,
        status: 'success',
        error: '',
        snapshotData: action.payload,
        snapshotRequestKey: action.requestKey,
      };
    case 'snapshotError':
      if (action.requestKey && state.snapshotRequestKey !== action.requestKey) {
        return state;
      }

      return {
        ...state,
        ...initialStockSnapshotLoadState,
        status: 'error',
        error: action.error,
        snapshotRequestKey: action.requestKey,
      };
    case 'setSnapshotDate':
      return {
        ...state,
        snapshotDate: action.value,
      };
    case 'setRedactionEnabled':
      return {
        ...state,
        redactionEnabled: action.value,
      };
    case 'setPriceHistoryState':
      return {
        ...state,
        priceHistoryState: action.state,
      };
    case 'setSnapshotPriceHistoryRange':
      return {
        ...state,
        snapshotPriceHistoryRange: action.value,
      };
    case 'toggleOutcomes':
      return {
        ...state,
        outcomesRevealed: !state.outcomesRevealed,
      };
    case 'setOutcomeState':
      return {
        ...state,
        outcomeState: action.state,
      };
    case 'setOutcomePriceHistoryState':
      return {
        ...state,
        outcomePriceHistoryState: action.state,
      };
    case 'setOutcomeCashFlowState':
      return {
        ...state,
        outcomeCashFlowState: action.state,
      };
    default:
      return state;
  }
}
const snapshotChartMetricConfig = {
  revenue: { label: 'Revenue', color: '#F1C646' },
  netIncome: { label: 'Net Income', color: '#5E6472' },
  operatingCashFlow: { label: 'Operating Cash Flow', color: '#3182CE' },
  capitalExpenditures: { label: 'Capital Expenditures', color: '#805AD5' },
};
const defaultSnapshotChartMetrics = ['revenue', 'netIncome', 'operatingCashFlow'];
const snapshotFilingFilterOptions = SEC_FILING_FILTER_OPTIONS.filter((option) => option.value !== 'custom');
const maxSnapshotPriceHistoryStartDate = '1900-01-01';
const snapshotPriceHistoryRangeOptions = [
  { value: '5y', label: '5Y', amount: 5, unit: 'year' },
  { value: '1y', label: '1Y', amount: 1, unit: 'year' },
  { value: 'max', label: 'Max' },
  { value: '6m', label: '6M', amount: 6, unit: 'month' },
  { value: '3m', label: '3M', amount: 3, unit: 'month' },
];
const defaultSnapshotReverseDcfAssumptions = {
  years: 8,
  wacc: 10,
  terminalGrowth: 3,
};
const snapshotReverseDcfLimits = {
  years: { min: 1, max: 20 },
  wacc: { min: 1, max: 30 },
  terminalGrowth: { min: -100, max: 100 },
};

const metricLabels = {
  revenue: 'Revenue',
  net_income: 'Net income',
  operating_cash_flow: 'Operating cash flow',
  capital_expenditures: 'Capital expenditures',
  cash: 'Cash',
  debt: 'Debt',
  shares_outstanding: 'Shares outstanding',
};

const snapshotStatementGroups = [
  {
    title: 'Income Statement',
    metrics: ['revenue', 'net_income'],
  },
  {
    title: 'Balance Sheet',
    metrics: ['cash', 'debt', 'shares_outstanding'],
  },
  {
    title: 'Statement of Cash Flows',
    metrics: ['operating_cash_flow', 'capital_expenditures'],
  },
];

function formatMetricLabel(metric) {
  return metricLabels[metric] || metric
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function isCompleteEmptyStateSnapshotDate(dateValue, maxSnapshotDate) {
  if (!isoDatePattern.test(dateValue || '')) {
    return false;
  }

  const year = Number(dateValue.slice(0, 4));
  return year >= minimumTypedSnapshotYear && (!maxSnapshotDate || dateValue <= maxSnapshotDate);
}

function formatNumber(value) {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return standardNumberFormatter.format(numericValue);
}

function buildSnapshotStatementRows(financials, metrics) {
  return metrics.flatMap((metric) => {
    const latest = financials?.[metric]?.latest;
    const periodLabel = latest?.period_end || 'Latest available';

    if (!latest) {
      return [];
    }

    return [{
      Metric: formatMetricLabel(metric),
      [periodLabel]: formatNumber(latest.value),
    }];
  });
}

function isSnapshotVisibleFact(latest, snapshotDate) {
  if (!latest?.filed_date || !snapshotDate) {
    return false;
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(latest.filed_date)
    && /^\d{4}-\d{2}-\d{2}$/.test(snapshotDate)
    && latest.filed_date <= snapshotDate;
}

function isSnapshotVisibleFiling(filing, snapshotDate) {
  if (!filing?.filingDate || !snapshotDate) {
    return false;
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(filing.filingDate)
    && /^\d{4}-\d{2}-\d{2}$/.test(snapshotDate)
    && filing.filingDate <= snapshotDate;
}

function filterSnapshotFinancials(financials, snapshotDate) {
  return Object.fromEntries(
    Object.entries(financials || {}).map(([metric, payload]) => {
      const latest = payload?.latest;
      const hasVisibleLatest = isSnapshotVisibleFact(latest, snapshotDate);

      return [
        metric,
        {
          ...payload,
          latest: hasVisibleLatest ? latest : null,
          available_count: hasVisibleLatest ? (payload?.available_count || 0) : 0,
        },
      ];
    })
  );
}

function compareSnapshotDateValues(a, b) {
  if (a === b) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }
  return a < b ? -1 : 1;
}

function compareSnapshotStringValues(a, b) {
  if (a === b) {
    return 0;
  }
  return String(a || '').localeCompare(String(b || ''));
}

const snapshotFilingSortComparators = {
  formType: (a, b) => compareSnapshotStringValues(a.formTypeKey, b.formTypeKey),
  filingDate: (a, b) => compareSnapshotDateValues(a.filingDateValue, b.filingDateValue),
  reportingDate: (a, b) => compareSnapshotDateValues(a.reportingDateValue, b.reportingDateValue),
};

function formatCurrency(value) {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return currencyNumberFormatter.format(numericValue);
}

function formatCompactCurrency(value) {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return compactCurrencyNumberFormatter.format(numericValue);
}

function formatChartCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return '-';
  }

  const numericValue = Number(value);
  const abs = Math.abs(numericValue);
  const sign = numericValue < 0 ? '-' : '';

  const format = (num, suffix = '') => {
    const formatted = num >= 10 ? num.toFixed(1) : num.toFixed(2);
    return `${sign}$${parseFloat(formatted).toString()}${suffix}`;
  };

  if (abs >= 1e12) return format(abs / 1e12, 'T');
  if (abs >= 1e9) return format(abs / 1e9, 'B');
  if (abs >= 1e6) return format(abs / 1e6, 'M');
  if (abs >= 1e3) return format(abs / 1e3, 'K');

  return `${sign}$${abs.toLocaleString()}`;
}

function formatFiscalYear(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }
  return date.getFullYear();
}

function formatFiscalLabel(dateString) {
  if (!dateString) return 'Fiscal period';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return `Fiscal period ended ${date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
}

async function loadSnapshotFinancialChartData(ticker, snapshotDate, signal) {
  const params = new URLSearchParams({ ticker });
  if (snapshotDate) {
    params.set('snapshot_date', snapshotDate);
  }
  const response = await fetch(
    `${chartApiBaseUrl}/chart-data?${params.toString()}`,
    {
      headers: { Accept: 'application/json' },
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`Chart data request failed with status ${response.status}.`);
  }

  return response.json();
}

async function loadSnapshotCashFlowOutcomes({ ticker, snapshotDate, signal }) {
  const query = new URLSearchParams({
    ticker,
    snapshot_date: snapshotDate,
  });
  const response = await fetch(`${apiBaseUrl}/api/snapshot-cash-flow-outcomes?${query.toString()}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(payload?.error || `Cash-flow outcome request failed with status ${response.status}.`);
  }

  return payload;
}

async function loadSnapshotLatestPrice({ ticker, signal }) {
  const query = new URLSearchParams({ ticker });
  const response = await fetch(`${apiBaseUrl}/api/stock-price?${query.toString()}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(payload?.error || `Latest price request failed with status ${response.status}.`);
  }

  const close = payload?.close == null ? NaN : Number(payload.close);
  if (
    payload?.price_status === 'unavailable'
    || !Number.isFinite(close)
    || close <= 0
  ) {
    throw new Error(payload?.price_message || 'Latest price is unavailable.');
  }

  return payload;
}

async function loadStockSnapshot({ ticker, snapshotDate, signal }) {
  const query = new URLSearchParams({
    ticker,
    snapshot_date: snapshotDate,
  });
  const response = await fetch(`${apiBaseUrl}/api/stock-snapshot?${query.toString()}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(payload?.error || `Snapshot request failed with status ${response.status}.`);
  }

  return payload;
}

function buildSnapshotPriceHistoryRange(snapshotDate, rangeKey = defaultSnapshotPriceHistoryRange) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(snapshotDate || '')) {
    return null;
  }

  const rangeOption = snapshotPriceHistoryRangeOptions.find((option) => option.value === rangeKey)
    || snapshotPriceHistoryRangeOptions.find((option) => option.value === defaultSnapshotPriceHistoryRange);

  if (rangeOption?.value === 'max') {
    return {
      startDate: maxSnapshotPriceHistoryStartDate,
      endDate: snapshotDate,
    };
  }

  const startDateValue = new Date(`${snapshotDate}T00:00:00Z`);
  if (Number.isNaN(startDateValue.getTime())) {
    return null;
  }

  if (rangeOption?.unit === 'month') {
    startDateValue.setUTCMonth(startDateValue.getUTCMonth() - rangeOption.amount);
  } else {
    startDateValue.setUTCFullYear(startDateValue.getUTCFullYear() - (rangeOption?.amount || 1));
  }

  return {
    startDate: startDateValue.toISOString().slice(0, 10),
    endDate: snapshotDate,
  };
}

function buildOutcomePriceHistoryRange(snapshotDate, endDate = todayIsoDate()) {
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(snapshotDate || '')
    || !/^\d{4}-\d{2}-\d{2}$/.test(endDate || '')
    || endDate < snapshotDate
  ) {
    return null;
  }

  return {
    startDate: snapshotDate,
    endDate,
  };
}

function buildStockSnapshotRequestKey(displayTicker, snapshotDate) {
  if (!displayTicker || !snapshotDate) {
    return '';
  }

  return `${displayTicker}:${snapshotDate}`;
}

function mergeSnapshotOutcomePriceHistory(snapshotPrices, outcomePrices) {
  const rowsByDate = new Map();

  for (const price of [
    ...(Array.isArray(snapshotPrices) ? snapshotPrices : []),
    ...(Array.isArray(outcomePrices) ? outcomePrices : []),
  ]) {
    if (price?.trading_date) {
      rowsByDate.set(price.trading_date, price);
    }
  }

  return Array.from(rowsByDate.values())
    .toSorted((a, b) => String(a.trading_date).localeCompare(String(b.trading_date)));
}

function buildCashFlowOutcomeRows(outcomeCashFlows, snapshotDate) {
  const definitions = [
    {
      key: 'operating_cash_flow',
      label: 'Operating cash flow',
    },
    {
      key: 'capital_expenditures',
      label: 'Capital expenditures',
    },
    {
      key: 'free_cash_flow',
      label: 'Free cash flow',
    },
  ];

  return definitions
    .flatMap(({ key, label }) => (outcomeCashFlows?.[key]?.facts || [])
      .flatMap((fact) => {
        if (
          !fact?.filed_date
          || !snapshotDate
          || !/^\d{4}-\d{2}-\d{2}$/.test(fact.filed_date)
          || fact.filed_date <= snapshotDate
        ) {
          return [];
        }

        return [{
          key: `${key}-${fact.period_end || fact.filed_date}`,
          metric: label,
          value: fact.value,
          periodEnd: fact.period_end,
          filedDate: fact.filed_date,
          form: fact.form,
        }];
      }))
    .sort((a, b) => {
      if (a.filedDate === b.filedDate) {
        return String(b.periodEnd || '').localeCompare(String(a.periodEnd || ''));
      }
      return String(b.filedDate || '').localeCompare(String(a.filedDate || ''));
    });
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return percentNumberFormatter.format(numericValue);
}

function formatSignedPercent(value) {
  const formatted = formatPercent(value);
  if (formatted === 'Not available' || Number(value) <= 0) {
    return formatted;
  }

  return `+${formatted}`;
}

function formatRatio(value) {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return `${ratioNumberFormatter.format(numericValue)}x`;
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizeSnapshotReverseDcfInput(value, field) {
  const fallback = defaultSnapshotReverseDcfAssumptions[field];
  const limits = snapshotReverseDcfLimits[field];
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  if (field === 'years') {
    return clampNumber(Math.round(numericValue), limits.min, limits.max);
  }

  return clampNumber(numericValue, limits.min, limits.max);
}

function getSnapshotNumericFact(financials, metric) {
  const value = financials?.[metric]?.latest?.value;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function getSnapshotFreeCashFlow(valuation, financials) {
  const valuationFcf = valuation?.p_fcf_ratio?.free_cash_flow?.value
    ?? valuation?.fcf_yield?.free_cash_flow?.value;
  const numericValuationFcf = Number(valuationFcf);
  if (Number.isFinite(numericValuationFcf)) {
    return numericValuationFcf;
  }

  const operatingCashFlow = getSnapshotNumericFact(financials, 'operating_cash_flow');
  const capitalExpenditures = getSnapshotNumericFact(financials, 'capital_expenditures');
  if (operatingCashFlow === null || capitalExpenditures === null) {
    return null;
  }

  return operatingCashFlow - capitalExpenditures;
}

function getSnapshotNetDebt(financials) {
  const debt = getSnapshotNumericFact(financials, 'debt');
  const cash = getSnapshotNumericFact(financials, 'cash');

  if (debt === null && cash === null) {
    return 0;
  }

  return (debt || 0) - (cash || 0);
}

function buildSnapshotHistoricalFcfRows(financials) {
  const operatingCashFlowFacts = financials?.operating_cash_flow?.facts || [];
  const capitalExpenditureFacts = financials?.capital_expenditures?.facts || [];
  const capexByPeriodEnd = new Map(
    capitalExpenditureFacts
      .flatMap((fact) => (fact?.period_end ? [[fact.period_end, fact]] : []))
  );

  return operatingCashFlowFacts
    .flatMap((operatingCashFlowFact) => {
      const periodEnd = operatingCashFlowFact?.period_end;
      const capitalExpendituresFact = capexByPeriodEnd.get(periodEnd);
      const operatingCashFlow = Number(operatingCashFlowFact?.value);
      const capitalExpenditures = Number(capitalExpendituresFact?.value);

      if (
        !periodEnd
        || !Number.isFinite(operatingCashFlow)
        || !Number.isFinite(capitalExpenditures)
      ) {
        return [];
      }

      return [{
        label: String(formatFiscalYear(periodEnd)),
        date: periodEnd,
        filedDate: [
          operatingCashFlowFact?.filed_date,
          capitalExpendituresFact?.filed_date,
        ].filter(Boolean).sort().at(-1) || null,
        year: formatFiscalYear(periodEnd),
        fcf: operatingCashFlow - capitalExpenditures,
        operatingCashFlow,
        capitalExpenditures,
      }];
    })
    .toSorted((a, b) => String(a.date).localeCompare(String(b.date)));
}

function calculateSnapshotReverseDcf({
  marketCap,
  netDebt,
  fcf,
  years = 8,
  wacc = 0.10,
  terminalGrowth = 0.03,
}) {
  const marketCapValue = Number(marketCap);
  const netDebtValue = Number(netDebt) || 0;
  const fcfValue = Number(fcf);

  if (!Number.isFinite(marketCapValue) || marketCapValue <= 0) {
    return { error: 'Snapshot market cap is unavailable.' };
  }

  if (!Number.isFinite(fcfValue) || fcfValue <= 0) {
    return { error: 'Reverse DCF needs positive snapshot free cash flow.' };
  }

  if (!Number.isFinite(wacc) || !Number.isFinite(terminalGrowth) || wacc <= terminalGrowth) {
    return { error: 'WACC must be greater than terminal growth.' };
  }

  const enterpriseValueTarget = marketCapValue + netDebtValue;
  const modelEnterpriseValue = (growth) => {
    let pvForecastTotal = 0;
    let lastFcf = fcfValue;

    for (let year = 1; year <= years; year += 1) {
      const projectedFcf = fcfValue * ((1 + growth) ** year);
      pvForecastTotal += projectedFcf / ((1 + wacc) ** year);
      lastFcf = projectedFcf;
    }

    const terminalValue = lastFcf * (1 + terminalGrowth) / (wacc - terminalGrowth);
    const pvTerminalValue = terminalValue / ((1 + wacc) ** years);
    return pvForecastTotal + pvTerminalValue;
  };

  let low = -0.9;
  let high = 2.0;
  let lowValue = modelEnterpriseValue(low);
  let highValue = modelEnterpriseValue(high);

  if ((lowValue - enterpriseValueTarget) * (highValue - enterpriseValueTarget) > 0) {
    return { error: 'Could not bracket an implied growth rate for these snapshot inputs.' };
  }

  let midpoint = 0;
  for (let index = 0; index < 80; index += 1) {
    midpoint = (low + high) / 2;
    const midpointValue = modelEnterpriseValue(midpoint);

    if (Math.abs(midpointValue - enterpriseValueTarget) < 1e-6) {
      break;
    }

    if ((lowValue - enterpriseValueTarget) * (midpointValue - enterpriseValueTarget) < 0) {
      high = midpoint;
      highValue = midpointValue;
    } else {
      low = midpoint;
      lowValue = midpointValue;
    }
  }

  const forecast = [];
  let pvForecastTotal = 0;
  let lastFcf = fcfValue;
  for (let year = 1; year <= years; year += 1) {
    const projectedFcf = fcfValue * ((1 + midpoint) ** year);
    const pvFcf = projectedFcf / ((1 + wacc) ** year);
    pvForecastTotal += pvFcf;
    lastFcf = projectedFcf;
    forecast.push({ year, fcf: projectedFcf, pv_fcf: pvFcf });
  }

  const terminalValue = lastFcf * (1 + terminalGrowth) / (wacc - terminalGrowth);
  const pvTerminalValue = terminalValue / ((1 + wacc) ** years);

  return {
    result: {
      impliedGrowth: midpoint,
      enterpriseValueTarget,
      pvForecastTotal,
      terminalValue,
      pvTerminalValue,
      pvTotal: pvForecastTotal + pvTerminalValue,
      forecast,
      assumptions: {
        years,
        wacc,
        terminalGrowth,
        marketCap: marketCapValue,
        netDebt: netDebtValue,
        fcf: fcfValue,
      },
    },
  };
}

function formatValuationStatus(status) {
  if (!status) {
    return 'Unavailable';
  }

  if (String(status).startsWith('negative_')) {
    return 'Negative';
  }

  return status
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function SnapshotFinancialChartPanel({ ticker, snapshotDate }) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');
  const gridStroke = useColorModeValue('#E2E8F0', '#2D3748');
  const tooltipBg = useColorModeValue('white', 'gray.800');
  const tooltipBorder = useColorModeValue('gray.200', 'gray.700');
  const tooltipText = useColorModeValue('gray.800', 'gray.100');
  const [chartState, setChartState] = useState(emptySnapshotFinancialChartState);

  useEffect(() => {
    if (!ticker || !snapshotDate) {
      setChartState(emptySnapshotFinancialChartState);
      return undefined;
    }

    const controller = new AbortController();

    async function loadChart() {
      setChartState({
        ...emptySnapshotFinancialChartState,
        status: 'loading',
      });

      try {
        const payload = await loadSnapshotFinancialChartData(ticker, snapshotDate, controller.signal);
        setChartState({
          status: 'success',
          chartData: payload?.data || [],
          metrics: payload?.metrics || [],
          error: '',
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        setChartState({
          ...emptySnapshotFinancialChartState,
          status: 'error',
          error: err.message || 'Unable to load financial chart data.',
        });
      }
    }

    loadChart();

    return () => controller.abort();
  }, [snapshotDate, ticker]);

  const metricKeys = useMemo(() => {
    const availableMetrics = chartState.metrics.length
      ? chartState.metrics
      : defaultSnapshotChartMetrics;
    return defaultSnapshotChartMetrics.filter((metricKey) => availableMetrics.includes(metricKey));
  }, [chartState.metrics]);

  const filteredChartData = useMemo(
    () => (chartState.chartData || [])
      .flatMap((row) => {
        if (!row?.date || row.date > snapshotDate) {
          return [];
        }

        const nextRow = { ...row };
        let hasVisibleMetric = false;
        metricKeys.forEach((metricKey) => {
          const filedDate = row.filed_dates?.[metricKey];
          if (filedDate && filedDate > snapshotDate) {
            delete nextRow[metricKey];
            return;
          }
          if (nextRow[metricKey] != null) {
            hasVisibleMetric = true;
          }
        });

        return hasVisibleMetric ? [nextRow] : [];
      }),
    [chartState.chartData, metricKeys, snapshotDate],
  );

  const legendPayload = useMemo(
    () => metricKeys.map((metricKey) => {
      const config = snapshotChartMetricConfig[metricKey] || {};
      return {
        value: config.label || metricKey,
        type: 'line',
        id: metricKey,
        color: config.color || '#8884d8',
        dataKey: metricKey,
      };
    }),
    [metricKeys],
  );

  const tooltipRenderer = useCallback(
    ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const visiblePayload = payload.filter((item) => (
        metricKeys.includes(item.dataKey) && item.value != null
      ));
      if (!visiblePayload.length) return null;

      return (
        <Box
          bg={tooltipBg}
          border="1px solid"
          borderColor={tooltipBorder}
          borderRadius="md"
          boxShadow="lg"
          p={3}
          color={tooltipText}
        >
          <Text fontWeight="semibold" mb={2}>
            {formatFiscalLabel(label)}
          </Text>
          {visiblePayload.map((item) => {
            const config = snapshotChartMetricConfig[item.dataKey] || {};
            return (
              <Flex key={item.dataKey} align="center" justify="space-between" gap={3} mb={1}>
                <HStack gap={2}>
                  <Box w="10px" h="10px" borderRadius="full" bg={config.color || item.color || 'gray'} />
                  <Text fontSize="sm">{config.label || item.name}</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="semibold">
                  {formatChartCurrency(item.value)}
                </Text>
              </Flex>
            );
          })}
        </Box>
      );
    },
    [metricKeys, tooltipBg, tooltipBorder, tooltipText],
  );

  const hasChartData = filteredChartData.length > 0 && metricKeys.length > 0;

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
      <Flex justify="space-between" align="center" gap={3} wrap="wrap" mb={4}>
        <Box>
          <Heading {...sectionHeadingProps}>Snapshot financial chart</Heading>
          <Text color={mutedText} fontSize="sm" mt={1}>
            Revenue, net income, and operating cash flow through {snapshotDate || 'the selected date'}.
          </Text>
        </Box>
        <Badge
          colorPalette={hasChartData ? 'green' : 'gray'}
          variant="subtle"
        >
          {chartState.status === 'loading' ? 'loading' : hasChartData ? '3 metrics' : chartState.status || 'idle'}
        </Badge>
      </Flex>

      {chartState.status === 'loading' ? (
        <Flex align="center" justify="center" gap={3} minH="240px">
          <Spinner size="sm" color="blue.500" />
          <Text color={mutedText} fontSize="sm">Loading financial chart…</Text>
        </Flex>
      ) : chartState.status === 'error' ? (
        <Alert.Root status="warning">
          <Alert.Indicator />
          <Alert.Content>{chartState.error || 'Unable to load financial chart data.'}</Alert.Content>
        </Alert.Root>
      ) : hasChartData ? (
        <Suspense
          fallback={
            <Flex align="center" justify="center" minH="360px">
              <Spinner size="sm" color="blue.500" />
            </Flex>
          }
        >
          <SnapshotFinancialRechartsChart
            chartData={filteredChartData}
            metricKeys={metricKeys}
            legendPayload={legendPayload}
            gridStroke={gridStroke}
            tooltipRenderer={tooltipRenderer}
          />
        </Suspense>
      ) : (
        <Text color={mutedText} fontSize="sm">
          Financial chart data is unavailable for this snapshot date.
        </Text>
      )}
    </Box>
  );
}

function SnapshotValuationRatios({ valuation }) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const ratios = [
    {
      key: 'market_cap',
      label: 'Market cap',
      payload: valuation?.market_cap,
      formatValue: formatCompactCurrency,
      availableDetail: 'Snapshot close x shares',
    },
    {
      key: 'pe_ratio',
      label: 'P/E',
      payload: valuation?.pe_ratio,
      formatValue: formatRatio,
    },
    {
      key: 'p_fcf_ratio',
      label: 'P/FCF',
      payload: valuation?.p_fcf_ratio,
      formatValue: formatRatio,
    },
    {
      key: 'fcf_yield',
      label: 'FCF yield',
      payload: valuation?.fcf_yield,
      formatValue: formatPercent,
    },
    {
      key: 'p_tangible_book_value',
      label: 'Price / tangible book',
      payload: valuation?.p_tangible_book_value,
      formatValue: formatRatio,
    },
  ];

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
      <Flex justify="space-between" align="center" gap={3} wrap="wrap" mb={4}>
        <Box>
          <Heading {...sectionHeadingProps}>Valuation ratios</Heading>
          <Text color={mutedText} fontSize="sm" mt={1}>
            Snapshot-safe ratios from price and filed financial facts.
          </Text>
        </Box>
        <Badge colorPalette={valuation ? 'blue' : 'gray'} variant="subtle">
          Snapshot inputs
        </Badge>
      </Flex>

      <SimpleGrid columns={{ base: 1, sm: 2, xl: 5 }} gap={3}>
        {ratios.map(({ key, label, payload, formatValue, availableDetail }) => {
          const isAvailable = payload?.status === 'available' && payload?.value !== null && payload?.value !== undefined;
          return (
            <Box key={key} borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
              <Text fontSize="sm" color={mutedText} fontWeight="semibold">{label}</Text>
              <Text fontSize="lg" fontWeight="bold" mt={1}>
                {isAvailable ? formatValue(payload.value) : formatValuationStatus(payload?.status)}
              </Text>
              <Text fontSize="xs" color={mutedText} mt={1}>
                {isAvailable ? availableDetail || 'Available' : payload?.message || 'Not available'}
              </Text>
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}

function SnapshotReverseDcfAssumptionControls({
  assumptions,
  borderColor,
  cardBg,
  mutedText,
  normalizeAssumption,
  setAssumption,
}) {
  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
        <Text as="label" htmlFor="snapshot-reverse-dcf-years" color={mutedText} fontSize="sm" fontWeight="semibold">
          Forecast years
        </Text>
        <Input
          id="snapshot-reverse-dcf-years"
          aria-label="Snapshot reverse DCF forecast years"
          type="number"
          inputMode="numeric"
          min={snapshotReverseDcfLimits.years.min}
          max={snapshotReverseDcfLimits.years.max}
          step={1}
          value={assumptions.years}
          onChange={(event) => setAssumption('years', event.target.value)}
          onBlur={() => normalizeAssumption('years')}
          mt={2}
        />
        <Text color={mutedText} fontSize="xs" mt={1}>
          Projection period used to solve implied FCF growth.
        </Text>
      </Box>
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
        <Text as="label" htmlFor="snapshot-reverse-dcf-wacc" color={mutedText} fontSize="sm" fontWeight="semibold">
          Discount rate (WACC %)
        </Text>
        <Input
          id="snapshot-reverse-dcf-wacc"
          aria-label="Snapshot reverse DCF discount rate"
          type="number"
          inputMode="decimal"
          min={snapshotReverseDcfLimits.wacc.min}
          max={snapshotReverseDcfLimits.wacc.max}
          step={0.25}
          value={assumptions.wacc}
          onChange={(event) => setAssumption('wacc', event.target.value)}
          onBlur={() => normalizeAssumption('wacc')}
          mt={2}
        />
        <Text color={mutedText} fontSize="xs" mt={1}>
          Required return used to discount forecast cash flows.
        </Text>
      </Box>
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
        <Text as="label" htmlFor="snapshot-reverse-dcf-terminal-growth" color={mutedText} fontSize="sm" fontWeight="semibold">
          Terminal growth rate (%)
        </Text>
        <Input
          id="snapshot-reverse-dcf-terminal-growth"
          aria-label="Snapshot reverse DCF terminal growth rate"
          type="number"
          inputMode="decimal"
          min={snapshotReverseDcfLimits.terminalGrowth.min}
          max={snapshotReverseDcfLimits.terminalGrowth.max}
          step={0.25}
          value={assumptions.terminalGrowth}
          onChange={(event) => setAssumption('terminalGrowth', event.target.value)}
          onBlur={() => normalizeAssumption('terminalGrowth')}
          mt={2}
        />
        <Text color={mutedText} fontSize="xs" mt={1}>
          Long-run FCF growth after the forecast period.
        </Text>
      </Box>
    </SimpleGrid>
  );
}

function SnapshotReverseDcfSummaryCards({ result, borderColor, cardBg, mutedText }) {
  return (
    <SimpleGrid columns={{ base: 1, md: 4 }} gap={3}>
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
        <Text color={mutedText} fontSize="sm" fontWeight="semibold">Implied FCF growth</Text>
        <Text fontSize="xl" fontWeight="bold" mt={1}>{formatPercent(result.impliedGrowth)}</Text>
        <Text color={mutedText} fontSize="xs" mt={1}>{result.assumptions.years} year forecast</Text>
      </Box>
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
        <Text color={mutedText} fontSize="sm" fontWeight="semibold">Snapshot FCF</Text>
        <Text fontSize="xl" fontWeight="bold" mt={1}>{formatCompactCurrency(result.assumptions.fcf)}</Text>
        <Text color={mutedText} fontSize="xs" mt={1}>OCF minus capex</Text>
      </Box>
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
        <Text color={mutedText} fontSize="sm" fontWeight="semibold">Target EV</Text>
        <Text fontSize="xl" fontWeight="bold" mt={1}>{formatCompactCurrency(result.enterpriseValueTarget)}</Text>
        <Text color={mutedText} fontSize="xs" mt={1}>Market cap plus net debt</Text>
      </Box>
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
        <Text color={mutedText} fontSize="sm" fontWeight="semibold">Terminal value PV</Text>
        <Text fontSize="xl" fontWeight="bold" mt={1}>{formatCompactCurrency(result.pvTerminalValue)}</Text>
        <Text color={mutedText} fontSize="xs" mt={1}>
          WACC {formatPercent(result.assumptions.wacc)}, terminal growth {formatPercent(result.assumptions.terminalGrowth)}
        </Text>
      </Box>
    </SimpleGrid>
  );
}

function SnapshotReverseDcfTable({ tableRows }) {
  return (
    <Box overflowX="auto">
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ backgroundColor: '#F7FAFC', textAlign: 'left', padding: '8px', borderBottom: '2px solid #CBD5E0' }}>Period</th>
            <th style={{ backgroundColor: '#F7FAFC', textAlign: 'left', padding: '8px', borderBottom: '2px solid #CBD5E0' }}>Type</th>
            <th style={{ backgroundColor: '#F7FAFC', textAlign: 'right', padding: '8px', borderBottom: '2px solid #CBD5E0' }}>FCF</th>
            <th style={{ backgroundColor: '#F7FAFC', textAlign: 'right', padding: '8px', borderBottom: '2px solid #CBD5E0' }}>PV of FCF</th>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row) => (
            <tr key={row.key} style={{ borderTop: '1px solid #EDF2F7' }}>
              <td style={{ padding: '8px' }}>{row.period}</td>
              <td style={{ padding: '8px' }}>{row.type}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{formatCompactCurrency(row.fcf)}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{row.pvFcf === null ? '-' : formatCompactCurrency(row.pvFcf)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

function SnapshotReverseDcfPanel({ data }) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const gridStroke = useColorModeValue('#E2E8F0', '#2D3748');
  const tooltipBg = useColorModeValue('white', 'gray.800');
  const tooltipBorder = useColorModeValue('gray.200', 'gray.700');
  const tooltipText = useColorModeValue('gray.800', 'gray.100');
  const [viewMode, setViewMode] = useState('graph');
  const [assumptions, setAssumptions] = useState(defaultSnapshotReverseDcfAssumptions);

  function setAssumption(field, value) {
    setAssumptions((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function normalizeAssumption(field) {
    setAssumptions((prev) => ({
      ...prev,
      [field]: normalizeSnapshotReverseDcfInput(prev[field], field),
    }));
  }

  const marketCap = data?.valuation?.market_cap?.status === 'available'
    ? Number(data.valuation.market_cap.value)
    : null;
  const freeCashFlow = getSnapshotFreeCashFlow(data?.valuation, data?.financials);
  const netDebt = getSnapshotNetDebt(data?.financials);
  const state = useMemo(() => {
    if (!data?.snapshot_date) {
      return emptySnapshotReverseDcfState;
    }

    const historicalFcf = buildSnapshotHistoricalFcfRows(data?.financials);
    const calculation = calculateSnapshotReverseDcf({
      marketCap,
      netDebt,
      fcf: freeCashFlow,
      years: normalizeSnapshotReverseDcfInput(assumptions.years, 'years'),
      wacc: normalizeSnapshotReverseDcfInput(assumptions.wacc, 'wacc') / 100,
      terminalGrowth: normalizeSnapshotReverseDcfInput(assumptions.terminalGrowth, 'terminalGrowth') / 100,
    });

    if (calculation.error) {
      return {
        ...emptySnapshotReverseDcfState,
        status: 'error',
        error: calculation.error,
      };
    }

    const snapshotYear = Number(data.snapshot_date.slice(0, 4));
    const chartData = [
      ...historicalFcf.map((row) => ({
        label: row.label,
        historicalFcf: row.fcf,
        impliedFcf: null,
        pvFcf: null,
        type: 'Historical',
        date: row.date,
        filedDate: row.filedDate,
        operatingCashFlow: row.operatingCashFlow,
        capitalExpenditures: row.capitalExpenditures,
      })),
      {
        label: 'Snapshot',
        historicalFcf: historicalFcf.at(-1)?.fcf ?? null,
        impliedFcf: freeCashFlow,
        pvFcf: null,
        type: 'Snapshot',
        date: data.snapshot_date,
      },
      ...calculation.result.forecast.map((row) => ({
        label: String(snapshotYear + row.year),
        historicalFcf: null,
        impliedFcf: row.fcf,
        pvFcf: row.pv_fcf,
        type: 'Market-implied',
        date: null,
      })),
    ];

    const tableRows = [
      ...historicalFcf.map((row) => ({
        key: `historical-${row.date}`,
        period: row.label,
        type: 'Historical',
        fcf: row.fcf,
        pvFcf: null,
      })),
      {
        key: 'snapshot',
        period: 'Snapshot',
        type: 'Baseline',
        fcf: freeCashFlow,
        pvFcf: null,
      },
      ...calculation.result.forecast.map((row) => ({
        key: `forecast-${row.year}`,
        period: String(snapshotYear + row.year),
        type: 'Market-implied',
        fcf: row.fcf,
        pvFcf: row.pv_fcf,
      })),
    ];

    return {
      status: 'success',
      chartData,
      tableRows,
      result: calculation.result,
      error: '',
    };
  }, [
    assumptions.terminalGrowth,
    assumptions.wacc,
    assumptions.years,
    data?.financials,
    data?.snapshot_date,
    freeCashFlow,
    marketCap,
    netDebt,
  ]);

  const tooltipRenderer = useCallback(({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0]?.payload;
    if (!point) return null;

    return (
      <Box
        bg={tooltipBg}
        borderWidth="1px"
        borderColor={tooltipBorder}
        borderRadius="md"
        boxShadow="md"
        color={tooltipText}
        p={3}
        minW="220px"
      >
        <Text fontSize="sm" fontWeight="bold">{label}</Text>
        <Text color={mutedText} fontSize="xs" mb={2}>{point.type}</Text>
        {point.historicalFcf !== null && point.historicalFcf !== undefined ? (
          <Text fontSize="sm">Historical FCF: {formatCompactCurrency(point.historicalFcf)}</Text>
        ) : null}
        {point.impliedFcf !== null && point.impliedFcf !== undefined ? (
          <Text fontSize="sm">Implied FCF: {formatCompactCurrency(point.impliedFcf)}</Text>
        ) : null}
        {point.pvFcf !== null && point.pvFcf !== undefined ? (
          <Text fontSize="sm">PV of FCF: {formatCompactCurrency(point.pvFcf)}</Text>
        ) : null}
        {point.operatingCashFlow !== undefined ? (
          <Text color={mutedText} fontSize="xs" mt={2}>
            OCF {formatCompactCurrency(point.operatingCashFlow)} minus capex {formatCompactCurrency(point.capitalExpenditures)}
          </Text>
        ) : null}
      </Box>
    );
  }, [mutedText, tooltipBg, tooltipBorder, tooltipText]);

  const result = state.result;
  const isLoading = state.status === 'loading';
  const isError = state.status === 'error';
  const hasData = state.status === 'success' && result;
  const assumptionControls = (
    <SnapshotReverseDcfAssumptionControls
      assumptions={assumptions}
      borderColor={borderColor}
      cardBg={cardBg}
      mutedText={mutedText}
      normalizeAssumption={normalizeAssumption}
      setAssumption={setAssumption}
    />
  );

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
      <Flex justify="space-between" align="center" gap={3} wrap="wrap" mb={4}>
        <Box>
          <Heading {...sectionHeadingProps}>Market-implied FCF path</Heading>
          <Text color={mutedText} fontSize="sm" mt={1}>
            Historical free cash flow beside the growth path implied by the snapshot valuation.
          </Text>
        </Box>
        <Badge colorPalette={hasData ? 'blue' : 'gray'} variant="subtle">
          Reverse DCF
        </Badge>
      </Flex>

      {isLoading ? (
        <Flex align="center" gap={3} minH="180px">
          <Spinner size="sm" color="blue.500" />
          <Text color={mutedText} fontSize="sm">Building market-implied FCF path…</Text>
        </Flex>
      ) : isError ? (
        <Stack gap={4}>
          {assumptionControls}
          <Alert.Root status="warning">
            <Alert.Indicator />
            <Alert.Content>{state.error || 'Unable to build the market-implied FCF path.'}</Alert.Content>
          </Alert.Root>
        </Stack>
      ) : hasData ? (
        <Stack gap={4}>
          {assumptionControls}

          <SnapshotReverseDcfSummaryCards
            result={result}
            borderColor={borderColor}
            cardBg={cardBg}
            mutedText={mutedText}
          />

          <Flex justify="space-between" align={{ base: 'stretch', sm: 'center' }} gap={3} direction={{ base: 'column', sm: 'row' }}>
            <Text color={mutedText} fontSize="sm">
              The projection is not a forecast. It is the FCF path required to make the snapshot price math work.
            </Text>
            <HStack gap={2}>
              <Button
                size="sm"
                variant={viewMode === 'graph' ? 'solid' : 'outline'}
                onClick={() => setViewMode('graph')}
              >
                Graph
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'table' ? 'solid' : 'outline'}
                onClick={() => setViewMode('table')}
              >
                Table
              </Button>
            </HStack>
          </Flex>

          {viewMode === 'graph' ? (
            <Suspense
              fallback={
                <Flex align="center" justify="center" minH="360px">
                  <Spinner size="sm" color="blue.500" />
                </Flex>
              }
            >
              <SnapshotReverseDcfRechartsChart
                chartData={state.chartData}
                gridStroke={gridStroke}
                tooltipRenderer={tooltipRenderer}
              />
            </Suspense>
          ) : (
            <SnapshotReverseDcfTable tableRows={state.tableRows} />
          )}
        </Stack>
      ) : (
        <Text color={mutedText} fontSize="sm">
          Market-implied FCF data is unavailable for this snapshot.
        </Text>
      )}
    </Box>
  );
}

function SnapshotEmptyState({ title, description }) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('gray.50', 'gray.800');

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
      <Heading size="xs">{title}</Heading>
      <Text color={mutedText} fontSize="sm" mt={2}>{description}</Text>
    </Box>
  );
}

function SnapshotCashFlowOutcomePanel({ snapshotDate, cashFlowOutcomeState }) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const rows = buildCashFlowOutcomeRows(
    cashFlowOutcomeState?.data?.outcome_cash_flows,
    snapshotDate,
  );
  const isLoading = cashFlowOutcomeState?.status === 'loading';
  const isError = cashFlowOutcomeState?.status === 'error';

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      bg={panelBg}
      p={5}
      data-testid="revealed-cash-flow-outcome"
    >
      <Flex justify="space-between" align="center" gap={3} wrap="wrap" mb={4}>
        <Box>
          <Heading {...sectionHeadingProps}>Revealed cash-flow outcome</Heading>
          <Text color={mutedText} fontSize="sm" mt={1}>
            Cash-flow facts filed after {snapshotDate || 'the snapshot date'}.
          </Text>
        </Box>
        <Badge colorPalette={rows.length ? 'green' : 'gray'} variant="subtle">
          {isLoading ? 'Loading' : rows.length ? `${rows.length} facts` : 'Unavailable'}
        </Badge>
      </Flex>

      {isLoading ? (
        <Flex align="center" gap={3}>
          <Spinner size="sm" color="blue.500" />
          <Text color={mutedText} fontSize="sm">Loading revealed cash-flow history…</Text>
        </Flex>
      ) : isError ? (
        <Text color={mutedText} fontSize="sm">
          {cashFlowOutcomeState?.error || 'Cash-flow outcomes are unavailable.'}
        </Text>
      ) : rows.length ? (
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
          {rows.map((row) => (
            <Box key={row.key} borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
              <Text color={mutedText} fontSize="sm" fontWeight="semibold">{row.metric}</Text>
              <Text fontSize="lg" fontWeight="bold" mt={1}>{formatNumber(row.value)}</Text>
              <Text color={mutedText} fontSize="sm" mt={1}>
                Period {row.periodEnd || 'Not available'}
              </Text>
              <Text color={mutedText} fontSize="sm">
                Filed {row.filedDate || 'Not available'}{row.form ? ` | ${row.form}` : ''}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      ) : (
        <Text color={mutedText} fontSize="sm">
          No post-snapshot cash-flow facts are available.
        </Text>
      )}
    </Box>
  );
}

function SnapshotOutcomePanel({ snapshotPrice, outcomeState }) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const currentPrice = outcomeState?.data || null;
  const snapshotClose = snapshotPrice?.close == null ? NaN : Number(snapshotPrice.close);
  const currentClose = currentPrice?.close == null ? NaN : Number(currentPrice.close);
  const hasComparison = Number.isFinite(snapshotClose)
    && snapshotClose > 0
    && Number.isFinite(currentClose)
    && currentClose > 0;
  const returnPct = hasComparison ? (currentClose - snapshotClose) / snapshotClose : null;

  if (!snapshotPrice?.close) {
    return null;
  }

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
      <Flex justify="space-between" align="center" gap={3} wrap="wrap" mb={4}>
        <Box>
          <Heading {...sectionHeadingProps}>Today vs snapshot</Heading>
          <Text color={mutedText} fontSize="sm" mt={1}>
            Outcome check shown only after identity is visible.
          </Text>
        </Box>
        <Badge colorPalette={hasComparison ? 'blue' : 'gray'} variant="subtle">
          {outcomeState?.status === 'loading' ? 'Loading' : hasComparison ? 'Available' : 'Unavailable'}
        </Badge>
      </Flex>

      {outcomeState?.status === 'loading' ? (
        <Flex align="center" gap={3}>
          <Spinner size="sm" color="blue.500" />
          <Text color={mutedText} fontSize="sm">Loading latest price…</Text>
        </Flex>
      ) : hasComparison ? (
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
          <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
            <Text color={mutedText} fontSize="sm" fontWeight="semibold">Snapshot close</Text>
            <Text fontSize="xl" fontWeight="bold" mt={1}>{formatCurrency(snapshotClose)}</Text>
            <Text color={mutedText} fontSize="sm" mt={1}>
              {snapshotPrice.trading_date || 'Snapshot date'}
            </Text>
          </Box>
          <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
            <Text color={mutedText} fontSize="sm" fontWeight="semibold">Latest close</Text>
            <Text fontSize="xl" fontWeight="bold" mt={1}>{formatCurrency(currentClose)}</Text>
            <Text color={mutedText} fontSize="sm" mt={1}>
              {currentPrice.trading_date || 'Latest cached date'}
            </Text>
          </Box>
          <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
            <Text color={mutedText} fontSize="sm" fontWeight="semibold">Price change</Text>
            <Text fontSize="xl" fontWeight="bold" mt={1}>{formatPercent(returnPct)}</Text>
            <Text color={mutedText} fontSize="sm" mt={1}>
              Excludes dividends and split adjustments unless reflected in provider data.
            </Text>
          </Box>
        </SimpleGrid>
      ) : (
        <Text color={mutedText} fontSize="sm">
          {outcomeState?.error || 'Latest price is unavailable, so the outcome comparison cannot be calculated.'}
        </Text>
      )}
    </Box>
  );
}

function buildSnapshotQuizPrompt({ data, snapshotFinancials, snapshotFilings, redactionEnabled }) {
  const companyName = redactionEnabled ? redactedCompanyName : data?.company?.name || 'Not available';
  const ticker = redactionEnabled ? redactedTicker : data?.ticker || 'Not available';
  const price = data?.price;
  const financialLines = Object.entries(snapshotFinancials || {})
    .flatMap(([metric, payload]) => {
      const latest = payload?.latest;
      return latest
        ? [[
            formatMetricLabel(metric),
            `value=${formatNumber(latest.value)}`,
            `period_end=${latest.period_end || 'not available'}`,
            `filed=${latest.filed_date || 'not available'}`,
            `form=${latest.form || 'not available'}`,
          ].join(', ')]
        : [];
    });
  const filingLines = (snapshotFilings || []).map((filing) => [
    filing.formType || filing.form_type || 'Filing',
    `filed=${filing.filingDate || filing.filing_date || 'not available'}`,
    `period=${filing.reportingDate || filing.reporting_date || 'not available'}`,
    filing.description ? `description=${filing.description}` : null,
  ].filter(Boolean).join(', '));
  const priceLine = price
    ? [
        `close=${formatCurrency(price.close)}`,
        price.adj_close !== undefined && price.adj_close !== null ? `adjusted_close=${formatCurrency(price.adj_close)}` : null,
        `trading_date=${price.trading_date || data?.snapshot_date || 'not available'}`,
      ].filter(Boolean).join(', ')
    : data?.price_message || 'Price unavailable';

  return [
    'You are running a hindsight-safe DCF Teacher snapshot quiz.',
    `Analyze only the data below that was available on or before ${data?.snapshot_date}.`,
    'Do not use current prices, later filings, later fiscal periods, news after the snapshot date, or outside company knowledge.',
    'Ask one concise investment-analysis quiz question and include a short answer key based only on the snapshot data.',
    '',
    `Company: ${companyName}`,
    `Ticker: ${ticker}`,
    `Snapshot date: ${data?.snapshot_date || 'not available'}`,
    `Snapshot price: ${priceLine}`,
    '',
    'Snapshot-visible financial facts:',
    financialLines.length ? financialLines.join('\n') : 'None available.',
    '',
    'Snapshot-visible SEC filings:',
    filingLines.length ? filingLines.join('\n') : 'None available.',
  ].join('\n');
}

function SnapshotQuizPanel({ data, snapshotFinancials, snapshotFilings, redactionEnabled }) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');
  const [quizPrompt, setQuizPrompt] = useState('');
  const [quizResponse, setQuizResponse] = useState('');
  const [quizStatus, setQuizStatus] = useState('idle');
  const [quizError, setQuizError] = useState('');
  const generatedPrompt = useMemo(
    () => buildSnapshotQuizPrompt({
      data,
      snapshotFinancials,
      snapshotFilings,
      redactionEnabled,
    }),
    [data, snapshotFinancials, snapshotFilings, redactionEnabled]
  );
  const effectivePrompt = quizPrompt.trim() || generatedPrompt;

  async function requestQuiz() {
    setQuizStatus('loading');
    setQuizError('');
    setQuizResponse('');

    try {
      const response = await fetch(`${apiBaseUrl}/api/ask`, {
        method: 'POST',
        headers: {
          Accept: 'text/plain',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: effectivePrompt }),
      });
      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || `Quiz request failed with status ${response.status}.`);
      }

      setQuizResponse(responseText);
      setQuizStatus('success');
    } catch (err) {
      setQuizError(err.message || 'Unable to generate a snapshot quiz.');
      setQuizStatus('error');
    }
  }

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
      <Flex justify="space-between" align="center" gap={3} wrap="wrap" mb={4}>
        <Box>
          <Heading {...sectionHeadingProps}>Snapshot quiz</Heading>
          <Text color={mutedText} fontSize="sm" mt={1}>
            Uses only the filings, facts, and price visible in this snapshot.
          </Text>
        </Box>
        <Button
          size="sm"
          colorPalette="blue"
          onClick={requestQuiz}
          loading={quizStatus === 'loading'}
          loadingText="Asking"
        >
          Ask LLM
        </Button>
      </Flex>

      <Textarea
        aria-label="Snapshot quiz prompt"
        value={quizPrompt}
        placeholder={generatedPrompt}
        onChange={(event) => setQuizPrompt(event.target.value)}
        minH="140px"
        mb={3}
      />
      <Text color={mutedText} fontSize="sm">
        Leave blank to use the generated hindsight-safe prompt.
      </Text>

      {quizStatus === 'error' ? (
        <Alert.Root status="error" mt={4}>
          <Alert.Indicator />
          <Alert.Content>{quizError}</Alert.Content>
        </Alert.Root>
      ) : null}

      {quizResponse ? (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4} mt={4}>
          <Text whiteSpace="pre-wrap">{quizResponse}</Text>
        </Box>
      ) : null}
    </Box>
  );
}

function SnapshotFinancialFactAvailability({
  financialEntries,
  visibleFinancialFactCount,
  snapshotDate,
  redactionEnabled,
}) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
      <Flex justify="space-between" align="center" gap={3} wrap="wrap" mb={4}>
        <Box>
          <Heading {...sectionHeadingProps}>Financial fact availability</Heading>
          <Text color={mutedText} fontSize="sm" mt={1}>
            Latest facts filed on or before {snapshotDate || 'the selected date'}.
          </Text>
        </Box>
        <Badge colorPalette={financialEntries.length ? 'green' : 'gray'} variant="subtle">
          {visibleFinancialFactCount} available metrics
        </Badge>
      </Flex>

      {financialEntries.length ? (
        <>
          {!visibleFinancialFactCount ? (
            <SnapshotEmptyState
              title="Insufficient SEC financial data"
              description={`No returned financial facts were filed on or before ${snapshotDate || 'the selected date'}. Statement tables will remain empty until a later filing is available.`}
            />
          ) : null}
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={3} mt={!visibleFinancialFactCount ? 3 : 0}>
            {financialEntries.map(([metric, payload]) => {
              const latest = payload?.latest;
              return (
                <Box key={metric} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4}>
                  <Flex justify="space-between" align="flex-start" gap={3}>
                    <Box>
                      <Text fontWeight="semibold">{formatMetricLabel(metric)}</Text>
                      <Text color={mutedText} fontSize="sm">
                        {payload?.available_count || 0} available facts
                      </Text>
                    </Box>
                    <Badge colorPalette={latest ? 'blue' : 'gray'} variant="subtle">
                      {latest?.form || 'No fact'}
                    </Badge>
                  </Flex>
                  {latest ? (
                    <Stack gap={1} mt={3} color={mutedText} fontSize="sm">
                      <Text color="inherit">Value: {formatNumber(latest.value)}</Text>
                      <Text color="inherit">Period end: {latest.period_end || 'Not available'}</Text>
                      <Text color="inherit">Filed: {latest.filed_date || 'Not available'}</Text>
                      {!redactionEnabled ? (
                        <>
                          <Text color="inherit">Source: {latest.source || 'SEC EDGAR'}</Text>
                          <Text color="inherit">Accession: {latest.accession_number || 'Not available'}</Text>
                        </>
                      ) : null}
                      {!redactionEnabled && latest.filing_url ? (
                        <Link
                          href={latest.filing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="blue.600"
                          fontWeight="semibold"
                          aria-label={`${formatMetricLabel(metric)} SEC source`}
                        >
                          SEC source
                        </Link>
                      ) : null}
                    </Stack>
                  ) : (
                    <Text color={mutedText} fontSize="sm" mt={3}>
                      No available fact as of this date.
                    </Text>
                  )}
                </Box>
              );
            })}
          </SimpleGrid>
        </>
      ) : (
        <SnapshotEmptyState
          title="Insufficient SEC financial data"
          description={`No financial fact metadata was returned for ${snapshotDate || 'the selected date'}. Try a later date or use the filings list to audit what was available.`}
        />
      )}
    </Box>
  );
}

function SnapshotFinancialStatementsPanel({ financialEntries, snapshotFinancials }) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
      <Flex justify="space-between" align="center" gap={3} wrap="wrap" mb={4}>
        <Box>
          <Heading {...sectionHeadingProps}>Snapshot financial statements</Heading>
          <Text color={mutedText} fontSize="sm" mt={1}>
            Statement-style view built from the latest snapshot-visible facts.
          </Text>
        </Box>
        <Badge colorPalette={financialEntries.length ? 'green' : 'gray'} variant="subtle">
          {financialEntries.length} facts
        </Badge>
      </Flex>

      <Stack gap={5}>
        {snapshotStatementGroups.map((statement) => (
          <Box key={statement.title}>
            <Heading {...subsectionHeadingProps}>{statement.title}</Heading>
            <FinancialStatementTable
              title={statement.title}
              data={buildSnapshotStatementRows(snapshotFinancials, statement.metrics)}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function SnapshotSecFilingsPanel({
  activeSnapshotFilingFilter,
  activeSnapshotFilingSort,
  cycleSnapshotFilingSortForColumn,
  hasSnapshotFilings,
  setSnapshotFilingFilter,
  snapshotDate,
  snapshotFilingFilter,
  snapshotFilings,
  sortedDisplayedSnapshotFilings,
}) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
      <Flex justify="space-between" align="center" gap={3} wrap="wrap" mb={4}>
        <Box>
          <Heading {...sectionHeadingProps}>Snapshot SEC filings</Heading>
          <Text color={mutedText} fontSize="sm" mt={1}>
            Filings available on or before {snapshotDate || 'the selected date'}.
          </Text>
        </Box>
        <Badge colorPalette={sortedDisplayedSnapshotFilings.length ? 'green' : 'gray'} variant="subtle">
          {sortedDisplayedSnapshotFilings.length} filings
        </Badge>
      </Flex>

      {hasSnapshotFilings ? (
        <Stack gap={3}>
          <Flex align="center" justify="space-between" gap={3} wrap="wrap">
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button size="sm" variant="outline">
                  <Flex align="center" gap={2}>
                    {activeSnapshotFilingFilter?.label || 'Filter'}
                    <LuChevronDown size={16} />
                  </Flex>
                </Button>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content>
                  {snapshotFilingFilterOptions.map((option) => (
                    <Menu.Item
                      key={option.value}
                      onClick={() => setSnapshotFilingFilter(option.value)}
                      fontWeight={option.value === snapshotFilingFilter ? 'semibold' : 'normal'}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                    >
                      {option.label}
                    </Menu.Item>
                  ))}
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
            <Text color={mutedText} fontSize="sm">
              Showing {sortedDisplayedSnapshotFilings.length} of {snapshotFilings.length} snapshot-visible filings
            </Text>
          </Flex>

          <FilingsTable
            activeSort={activeSnapshotFilingSort}
            columns={SEC_FILING_COLUMNS}
            cycleSortForColumn={cycleSnapshotFilingSortForColumn}
            filings={snapshotFilings}
            hasFilings={sortedDisplayedSnapshotFilings.length > 0}
            isLoading={false}
            sortedFilings={sortedDisplayedSnapshotFilings}
          />
        </Stack>
      ) : (
        <SnapshotEmptyState
          title="No SEC filings yet"
          description={`No filings were available on or before ${snapshotDate || 'the selected date'}. For very old snapshot dates, move forward to the company's first public filing date.`}
        />
      )}
    </Box>
  );
}

function SnapshotSummary({
  data,
  redactionEnabled = false,
  outcomesRevealed = false,
  priceHistoryRange = defaultSnapshotPriceHistoryRange,
  onPriceHistoryRangeChange = () => {},
  outcomeState,
  priceHistoryState = emptyPriceHistoryState,
  outcomePriceHistoryState = emptyPriceHistoryState,
  outcomeCashFlowState = emptyCashFlowOutcomeState,
}) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');
  const emphasisText = useColorModeValue('gray.800', 'gray.100');
  const [snapshotFilingFilter, setSnapshotFilingFilter] = useState('exclude-insider');
  const [snapshotFilingSort, setSnapshotFilingSort] = useState({
    ...DEFAULT_SEC_FILING_SORT,
    isDefault: true,
  });
  const activeSnapshotFilingFilter = getSecFilingFilterOption(snapshotFilingFilter);
  const snapshotFinancials = useMemo(
    () => filterSnapshotFinancials(data?.financials, data?.snapshot_date),
    [data?.financials, data?.snapshot_date]
  );
  const financialEntries = Object.entries(snapshotFinancials);
  const snapshotFilings = useMemo(
    () => normaliseFilingsPayload(data?.filings || [])
      .filter((filing) => isSnapshotVisibleFiling(filing, data?.snapshot_date)),
    [data?.filings, data?.snapshot_date]
  );
  const filteredSnapshotFilings = useMemo(
    () => {
      const predicate = activeSnapshotFilingFilter?.predicate || (() => true);
      return snapshotFilings.filter((filing) => {
        try {
          return predicate(filing);
        } catch (err) {
          return true;
        }
      });
    },
    [activeSnapshotFilingFilter, snapshotFilings]
  );
  const activeSnapshotFilingSort = snapshotFilingSort.isDefault
    ? DEFAULT_SEC_FILING_SORT
    : {
        column: snapshotFilingSort.column,
        direction: snapshotFilingSort.direction,
      };
  const sortedSnapshotFilings = useMemo(
    () => {
      const comparator = snapshotFilingSortComparators[activeSnapshotFilingSort.column];
      if (!comparator) {
        return filteredSnapshotFilings;
      }

      const multiplier = activeSnapshotFilingSort.direction === 'desc' ? -1 : 1;
      return filteredSnapshotFilings.toSorted((a, b) => comparator(a, b) * multiplier);
    },
    [activeSnapshotFilingSort.column, activeSnapshotFilingSort.direction, filteredSnapshotFilings]
  );
  const sortedDisplayedSnapshotFilings = useMemo(
    () => redactionEnabled
      ? sortedSnapshotFilings.map((filing) => ({
          ...filing,
          href: null,
          filingUrl: null,
        }))
      : sortedSnapshotFilings,
    [redactionEnabled, sortedSnapshotFilings]
  );
  const cycleSnapshotFilingSortForColumn = useCallback((columnKey, isSortable) => {
    if (!isSortable) {
      return;
    }

    setSnapshotFilingSort((prev) => {
      if (prev.isDefault && columnKey === DEFAULT_SEC_FILING_SORT.column) {
        return { column: columnKey, direction: 'asc', isDefault: false };
      }

      if (prev.column !== columnKey) {
        return { column: columnKey, direction: 'asc', isDefault: false };
      }

      if (prev.direction === 'asc') {
        return { column: columnKey, direction: 'desc', isDefault: false };
      }

      return { ...DEFAULT_SEC_FILING_SORT, isDefault: true };
    });
  }, []);
  const priceStatus = data?.price_status || 'unknown';
  const price = data?.price || null;
  const snapshotPriceHistory = useMemo(
    () => (priceHistoryState?.prices || [])
      .filter((priceRow) => (
        priceRow?.trading_date
        && data?.snapshot_date
        && priceRow.trading_date <= data.snapshot_date
      )),
    [data?.snapshot_date, priceHistoryState?.prices]
  );
  const revealedPriceHistory = useMemo(
    () => mergeSnapshotOutcomePriceHistory(
      snapshotPriceHistory,
      outcomePriceHistoryState?.prices || [],
    ),
    [outcomePriceHistoryState?.prices, snapshotPriceHistory],
  );
  const chartPrices = outcomesRevealed && !redactionEnabled
    ? revealedPriceHistory
    : snapshotPriceHistory;
  const chartStatus = outcomesRevealed && !redactionEnabled
    ? outcomePriceHistoryState?.priceStatus || priceHistoryState?.priceStatus
    : priceHistoryState?.priceStatus;
  const chartStateStatus = outcomesRevealed && !redactionEnabled
    ? outcomePriceHistoryState?.status || priceHistoryState?.status
    : priceHistoryState?.status;
  const chartMessage = outcomesRevealed && !redactionEnabled
    ? outcomePriceHistoryState?.priceMessage || priceHistoryState?.priceMessage
    : priceHistoryState?.priceMessage;
  const chartError = outcomesRevealed && !redactionEnabled && outcomePriceHistoryState?.status === 'error'
    ? null
    : priceHistoryState?.status === 'error' ? priceHistoryState.error : null;
  const chartLoading = priceHistoryState?.status === 'loading'
    || (outcomesRevealed && !redactionEnabled && outcomePriceHistoryState?.status === 'loading');
  const snapshotClose = Number(price?.close);
  const latestClose = Number(outcomeState?.data?.close);
  const hasOutcomePriceChange = outcomesRevealed
    && !redactionEnabled
    && Number.isFinite(snapshotClose)
    && snapshotClose !== 0
    && Number.isFinite(latestClose);
  const outcomePriceChangePct = hasOutcomePriceChange
    ? (latestClose - snapshotClose) / snapshotClose
    : null;
  const visibleFinancialFactCount = financialEntries.filter(([, payload]) => payload?.latest).length;
  const hasSnapshotFilings = snapshotFilings.length > 0;
  const hasSnapshotVisibleSecData = hasSnapshotFilings || visibleFinancialFactCount > 0;
  const isPriceUnavailable = priceStatus === 'unavailable' || !price;

  return (
    <Stack gap={5}>
      {!hasSnapshotVisibleSecData ? (
        <Alert.Root status="info">
          <Alert.Indicator />
          <Alert.Content>
            No SEC filings or financial facts are available on or before {data?.snapshot_date || 'this date'}. Try a later snapshot date after the company had public filings.
          </Alert.Content>
        </Alert.Root>
      ) : null}

      {isPriceUnavailable ? (
        <Alert.Root status="warning">
          <Alert.Indicator />
          <Alert.Content>
            Historical price is unavailable for this snapshot. {data?.price_message || 'The snapshot can still be reviewed using available SEC data.'}
          </Alert.Content>
        </Alert.Root>
      ) : null}

      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
        <Flex justify="space-between" align="center" gap={3} wrap="wrap" mb={4}>
          <Box>
            <Heading {...sectionHeadingProps}>
              {outcomesRevealed && !redactionEnabled ? 'Revealed price history' : 'Pre-snapshot price history'}
            </Heading>
            <Text color={mutedText} fontSize="sm" mt={1}>
              {outcomesRevealed && !redactionEnabled
                ? `Prices expanded past ${data?.snapshot_date || 'the selected date'} through the latest available date.`
                : `Prices shown only through ${data?.snapshot_date || 'the selected date'}.`}
            </Text>
            {hasOutcomePriceChange ? (
              <Text color={mutedText} fontSize="sm" mt={2}>
                Stock price change since snapshot: <Text as="span" fontWeight="semibold" color={emphasisText}>{formatSignedPercent(outcomePriceChangePct)}</Text>
                {' '}
                ({formatCurrency(snapshotClose)} to {formatCurrency(latestClose)}
                {outcomeState?.data?.trading_date ? ` as of ${outcomeState.data.trading_date}` : ''})
              </Text>
            ) : null}
          </Box>
          <Stack align={{ base: 'stretch', sm: 'flex-end' }} gap={2}>
            <Badge
              alignSelf={{ base: 'flex-start', sm: 'flex-end' }}
              colorPalette={chartStatus === 'available' ? 'green' : 'gray'}
              variant="subtle"
            >
              {chartStatus || chartStateStatus || 'idle'}
            </Badge>
            <HStack gap={1} wrap="wrap" aria-label="Pre-snapshot price history range">
              {snapshotPriceHistoryRangeOptions.map((option) => {
                const isSelected = option.value === priceHistoryRange;
                return (
                  <Button
                    key={option.value}
                    type="button"
                    size="xs"
                    variant={isSelected ? 'solid' : 'outline'}
                    colorPalette={isSelected ? 'blue' : 'gray'}
                    onClick={() => onPriceHistoryRangeChange(option.value)}
                    aria-pressed={isSelected}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </HStack>
          </Stack>
        </Flex>

        <StockPriceHistoryChart
          prices={chartPrices}
          loading={chartLoading}
          error={chartError}
          redactionEnabled={redactionEnabled}
          priceStatus={chartStatus}
          priceMessage={chartMessage}
          referenceDate={outcomesRevealed && !redactionEnabled ? data?.snapshot_date : null}
          referenceLabel="Snapshot date"
          preferAdjustedClose
        />
      </Box>

      <SnapshotFinancialChartPanel
        ticker={data?.ticker}
        snapshotDate={data?.snapshot_date}
      />

      <SnapshotValuationRatios valuation={data?.valuation} />

      <SnapshotReverseDcfPanel data={data} />

      {outcomesRevealed && !redactionEnabled && !isPriceUnavailable ? (
        <SnapshotOutcomePanel
          snapshotPrice={price}
          outcomeState={outcomeState}
        />
      ) : null}

      {outcomesRevealed && !redactionEnabled ? (
        <SnapshotCashFlowOutcomePanel
          snapshotDate={data?.snapshot_date}
          cashFlowOutcomeState={outcomeCashFlowState}
        />
      ) : null}

      <SnapshotQuizPanel
        data={data}
        snapshotFinancials={snapshotFinancials}
        snapshotFilings={snapshotFilings}
        redactionEnabled={redactionEnabled}
      />

      <SnapshotFinancialFactAvailability
        financialEntries={financialEntries}
        visibleFinancialFactCount={visibleFinancialFactCount}
        snapshotDate={data?.snapshot_date}
        redactionEnabled={redactionEnabled}
      />

      <SnapshotFinancialStatementsPanel
        financialEntries={financialEntries}
        snapshotFinancials={snapshotFinancials}
      />

      <SnapshotSecFilingsPanel
        activeSnapshotFilingFilter={activeSnapshotFilingFilter}
        activeSnapshotFilingSort={activeSnapshotFilingSort}
        cycleSnapshotFilingSortForColumn={cycleSnapshotFilingSortForColumn}
        hasSnapshotFilings={hasSnapshotFilings}
        setSnapshotFilingFilter={setSnapshotFilingFilter}
        snapshotDate={data?.snapshot_date}
        snapshotFilingFilter={snapshotFilingFilter}
        snapshotFilings={snapshotFilings}
        sortedDisplayedSnapshotFilings={sortedDisplayedSnapshotFilings}
      />
    </Stack>
  );
}

function SnapshotStatePanel({
  status,
  error,
  hasDate,
  data,
  redactionEnabled = false,
  outcomesRevealed = false,
  outcomeState,
  priceHistoryState = emptyPriceHistoryState,
  priceHistoryRange = defaultSnapshotPriceHistoryRange,
  onPriceHistoryRangeChange = () => {},
  outcomePriceHistoryState = emptyPriceHistoryState,
  outcomeCashFlowState = emptyCashFlowOutcomeState,
  snapshotDate = '',
  maxSnapshotDate,
  onSnapshotDateChange,
}) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.900');

  function handleEmptyDateChange(event) {
    const nextDate = event.target.value;

    if (!nextDate || isCompleteEmptyStateSnapshotDate(nextDate, maxSnapshotDate)) {
      onSnapshotDateChange?.(nextDate);
    }
  }

  if (status === 'loading') {
    return (
      <Flex
        align="center"
        gap={3}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        bg={panelBg}
        p={5}
      >
        <Spinner size="sm" color="blue.500" />
        <Text color={mutedText}>Loading snapshot data…</Text>
      </Flex>
    );
  }

  if (status === 'error') {
    return (
      <Alert.Root status="error">
        <Alert.Indicator />
        <Alert.Content>{error || 'Unable to load snapshot data.'}</Alert.Content>
      </Alert.Root>
    );
  }

  if (!hasDate) {
    return (
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
        <Heading {...sectionHeadingProps} mb={2}>No snapshot date selected</Heading>
        <Text color={mutedText}>
          Choose a date to build a historical view for this stock.
        </Text>
        <VStack align="stretch" gap={2} maxW="320px" mx="auto" mt={5}>
          <HStack gap={2} color={mutedText} fontWeight="semibold" fontSize="sm">
            <FiCalendar />
            <Text>Snapshot date</Text>
          </HStack>
          <Input
            id="empty-snapshot-date"
            aria-label="Choose date for snapshot"
            type="date"
            value={snapshotDate}
            min={`${minimumTypedSnapshotYear}-01-01`}
            max={maxSnapshotDate}
            onChange={handleEmptyDateChange}
          />
        </VStack>
      </Box>
    );
  }

  if (status === 'success' && data) {
    return (
      <SnapshotSummary
        data={data}
        redactionEnabled={redactionEnabled}
        outcomesRevealed={outcomesRevealed}
        priceHistoryRange={priceHistoryRange}
        onPriceHistoryRangeChange={onPriceHistoryRangeChange}
        outcomeState={outcomeState}
        priceHistoryState={priceHistoryState}
        outcomePriceHistoryState={outcomePriceHistoryState}
        outcomeCashFlowState={outcomeCashFlowState}
      />
    );
  }

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} p={5}>
      <Heading {...sectionHeadingProps} mb={2}>Snapshot summary pending</Heading>
      <Text color={mutedText}>
        Company details, filings, financial facts, and price status will appear here.
      </Text>
    </Box>
  );
}

function useStockSnapshotController(displayTicker, maxSnapshotDate) {
  const [snapshotState, dispatchSnapshotState] = useReducer(
    stockSnapshotReducer,
    initialStockSnapshotState,
  );
  const {
    snapshotDate,
    snapshotData,
    snapshotPriceHistoryRange,
    outcomesRevealed,
    redactionEnabled,
  } = snapshotState;
  const snapshotRequestKey = buildStockSnapshotRequestKey(displayTicker, snapshotDate);
  const resolvedSnapshotState = useMemo(() => {
    if (!snapshotRequestKey) {
      return {
        ...snapshotState,
        ...initialStockSnapshotLoadState,
      };
    }

    if (snapshotState.snapshotRequestKey !== snapshotRequestKey) {
      return {
        ...snapshotState,
        ...initialStockSnapshotLoadState,
        status: 'loading',
        snapshotRequestKey,
      };
    }

    return snapshotState;
  }, [snapshotRequestKey, snapshotState]);

  useEffect(() => {
    if (!snapshotRequestKey) {
      return undefined;
    }

    const controller = new AbortController();

    async function loadSnapshot() {
      dispatchSnapshotState({ type: 'snapshotLoading', requestKey: snapshotRequestKey });

      try {
        const payload = await loadStockSnapshot({
          ticker: displayTicker,
          snapshotDate,
          signal: controller.signal,
        });

        dispatchSnapshotState({ type: 'snapshotSuccess', payload, requestKey: snapshotRequestKey });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        dispatchSnapshotState({
          type: 'snapshotError',
          error: err.message || 'Unable to load snapshot data.',
          requestKey: snapshotRequestKey,
        });
      }
    }

    loadSnapshot();

    return () => controller.abort();
  }, [displayTicker, snapshotDate, snapshotRequestKey]);

  useEffect(() => {
    const range = buildSnapshotPriceHistoryRange(snapshotDate, snapshotPriceHistoryRange);

    if (!displayTicker || !range) {
      dispatchSnapshotState({
        type: 'setPriceHistoryState',
        state: emptyPriceHistoryState,
      });
      return undefined;
    }

    const controller = new AbortController();

    async function loadPriceHistory() {
      dispatchSnapshotState({
        type: 'setPriceHistoryState',
        state: {
          ...emptyPriceHistoryState,
          status: 'loading',
          startDate: range.startDate,
          endDate: range.endDate,
        },
      });

      try {
        const payload = await getStockPriceHistory({
          ticker: displayTicker,
          startDate: range.startDate,
          endDate: range.endDate,
          signal: controller.signal,
        });

        dispatchSnapshotState({
          type: 'setPriceHistoryState',
          state: {
            status: 'success',
            prices: payload?.prices || [],
            error: '',
            priceStatus: payload?.price_status || null,
            priceMessage: payload?.price_message || null,
            startDate: payload?.start_date || range.startDate,
            endDate: payload?.end_date || range.endDate,
          },
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        dispatchSnapshotState({
          type: 'setPriceHistoryState',
          state: {
            ...emptyPriceHistoryState,
            status: 'error',
            error: err.message || 'Unable to load snapshot price history.',
            startDate: range.startDate,
            endDate: range.endDate,
          },
        });
      }
    }

    loadPriceHistory();

    return () => controller.abort();
  }, [displayTicker, snapshotDate, snapshotPriceHistoryRange]);

  useEffect(() => {
    const canLoadOutcome = displayTicker
      && snapshotData?.price?.close
      && outcomesRevealed
      && !redactionEnabled;

    if (!canLoadOutcome) {
      dispatchSnapshotState({
        type: 'setOutcomeState',
        state: emptySnapshotOutcomeState,
      });
      return undefined;
    }

    const controller = new AbortController();

    async function loadCurrentPrice() {
      dispatchSnapshotState({
        type: 'setOutcomeState',
        state: { status: 'loading', data: null, error: '' },
      });

      try {
        const payload = await loadSnapshotLatestPrice({
          ticker: displayTicker,
          signal: controller.signal,
        });

        dispatchSnapshotState({
          type: 'setOutcomeState',
          state: { status: 'success', data: payload, error: '' },
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        dispatchSnapshotState({
          type: 'setOutcomeState',
          state: {
            status: 'error',
            data: null,
            error: err.message || 'Latest price is unavailable.',
          },
        });
      }
    }

    loadCurrentPrice();

    return () => controller.abort();
  }, [displayTicker, outcomesRevealed, redactionEnabled, snapshotData?.price?.close]);

  useEffect(() => {
    const range = buildOutcomePriceHistoryRange(snapshotData?.snapshot_date, maxSnapshotDate);
    const canLoadOutcomeHistory = displayTicker
      && outcomesRevealed
      && !redactionEnabled
      && snapshotData?.price?.close
      && range;

    if (!canLoadOutcomeHistory) {
      dispatchSnapshotState({
        type: 'setOutcomePriceHistoryState',
        state: emptyPriceHistoryState,
      });
      return undefined;
    }

    const controller = new AbortController();

    async function loadOutcomePriceHistory() {
      dispatchSnapshotState({
        type: 'setOutcomePriceHistoryState',
        state: {
          ...emptyPriceHistoryState,
          status: 'loading',
          startDate: range.startDate,
          endDate: range.endDate,
        },
      });

      try {
        const payload = await getStockPriceHistory({
          ticker: displayTicker,
          startDate: range.startDate,
          endDate: range.endDate,
          signal: controller.signal,
        });

        dispatchSnapshotState({
          type: 'setOutcomePriceHistoryState',
          state: {
            status: 'success',
            prices: payload?.prices || [],
            error: '',
            priceStatus: payload?.price_status || null,
            priceMessage: payload?.price_message || null,
            startDate: payload?.start_date || range.startDate,
            endDate: payload?.end_date || range.endDate,
          },
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        dispatchSnapshotState({
          type: 'setOutcomePriceHistoryState',
          state: {
            ...emptyPriceHistoryState,
            status: 'error',
            error: err.message || 'Unable to load revealed price outcome.',
            startDate: range.startDate,
            endDate: range.endDate,
          },
        });
      }
    }

    loadOutcomePriceHistory();

    return () => controller.abort();
  }, [
    displayTicker,
    maxSnapshotDate,
    outcomesRevealed,
    redactionEnabled,
    snapshotData?.price?.close,
    snapshotData?.snapshot_date,
  ]);

  useEffect(() => {
    const canLoadCashFlowOutcomes = displayTicker
      && outcomesRevealed
      && !redactionEnabled
      && snapshotData?.snapshot_date;

    if (!canLoadCashFlowOutcomes) {
      dispatchSnapshotState({
        type: 'setOutcomeCashFlowState',
        state: emptyCashFlowOutcomeState,
      });
      return undefined;
    }

    const controller = new AbortController();

    async function loadCashFlowOutcomes() {
      dispatchSnapshotState({
        type: 'setOutcomeCashFlowState',
        state: { status: 'loading', data: null, error: '' },
      });

      try {
        const payload = await loadSnapshotCashFlowOutcomes({
          ticker: displayTicker,
          snapshotDate: snapshotData.snapshot_date,
          signal: controller.signal,
        });

        dispatchSnapshotState({
          type: 'setOutcomeCashFlowState',
          state: { status: 'success', data: payload, error: '' },
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        dispatchSnapshotState({
          type: 'setOutcomeCashFlowState',
          state: {
            status: 'error',
            data: null,
            error: err.message || 'Unable to load revealed cash-flow history.',
          },
        });
      }
    }

    loadCashFlowOutcomes();

    return () => controller.abort();
  }, [
    displayTicker,
    outcomesRevealed,
    redactionEnabled,
    snapshotData?.snapshot_date,
  ]);

  return [resolvedSnapshotState, dispatchSnapshotState];
}

function StockSnapshotHeader({
  displayTicker,
  snapshotData,
  snapshotDate,
  maxSnapshotDate,
  status,
  redactionEnabled,
  outcomesRevealed,
  isAuthenticated = false,
  onSnapshotDateChange,
  onToggleOutcomes,
  onRequireLogin,
  onRedactionChange,
}) {
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('white', 'gray.900');
  const hasSnapshotDate = Boolean(snapshotDate);
  const headerTicker = redactionEnabled ? redactedTicker : displayTicker || 'STOCK';
  const headerTitle = redactionEnabled ? `${redactedCompanyName} Snapshot` : `${displayTicker} Snapshot`;
  const headerCompanyName = redactionEnabled
    ? redactedCompanyName
    : snapshotData?.company?.name || '';
  const headerCik = !redactionEnabled ? snapshotData?.company?.cik : '';
  const handleRevealOutcomesClick = () => {
    if (!outcomesRevealed && !isAuthenticated) {
      onRequireLogin?.();
      return;
    }
    onToggleOutcomes?.();
  };

  return (
    <Flex
      direction={{ base: 'column', lg: 'row' }}
      justify="space-between"
      align={{ base: 'stretch', lg: 'flex-end' }}
      gap={4}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      bg={headerBg}
      p={{ base: 4, md: 5 }}
      mb={5}
    >
      <Box>
        <HStack gap={2} mb={2} wrap="wrap">
          <Badge colorPalette="blue" variant="subtle">{headerTicker}</Badge>
          <Badge colorPalette={hasSnapshotDate ? 'green' : 'gray'} variant="subtle">
            {hasSnapshotDate ? snapshotDate : 'No date'}
          </Badge>
        </HStack>
        <Heading size="lg">{headerTitle}</Heading>
        {headerCompanyName ? (
          <HStack gap={2} mt={2} wrap="wrap" color={mutedText} fontSize="sm">
            <Text fontWeight="semibold">{headerCompanyName}</Text>
            {headerCik ? (
              <Text>CIK {headerCik}</Text>
            ) : null}
          </HStack>
        ) : null}
        <Text color={mutedText} mt={2}>
          Historical view using information available as of the selected date.
        </Text>
      </Box>

      <VStack align="stretch" gap={3} w={{ base: '100%', sm: '260px' }}>
        <HStack gap={2} color={mutedText} fontWeight="semibold" fontSize="sm">
          <FiCalendar />
          <Text as="label" htmlFor="snapshot-date">Snapshot date</Text>
        </HStack>
        <Input
          id="snapshot-date"
          type="date"
          value={snapshotDate}
          max={maxSnapshotDate}
          onChange={(event) => onSnapshotDateChange(event.target.value)}
        />
        <Button
          type="button"
          colorPalette={outcomesRevealed ? 'green' : 'orange'}
          variant={outcomesRevealed ? 'subtle' : 'solid'}
          disabled={!hasSnapshotDate || status !== 'success'}
          onClick={handleRevealOutcomesClick}
        >
          {outcomesRevealed ? <FiEyeOff /> : <FiEye />}
          {outcomesRevealed ? 'Hide outcomes' : 'Reveal outcomes'}
        </Button>
        <Switch.Root
          size="sm"
          checked={redactionEnabled}
          onCheckedChange={(event) => onRedactionChange(event.checked)}
        >
          <Switch.HiddenInput aria-label="Redaction mode" />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label fontSize="sm" color={mutedText}>
            Redaction mode
          </Switch.Label>
        </Switch.Root>
      </VStack>
    </Flex>
  );
}

function StockSnapshot() {
  const { ticker = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const displayTicker = ticker.toUpperCase();
  const maxSnapshotDate = useMemo(() => todayIsoDate(), []);
  const [snapshotState, dispatchSnapshotState] = useStockSnapshotController(displayTicker, maxSnapshotDate);
  const {
    status,
    error,
    snapshotData,
    outcomesRevealed,
    outcomeState,
    outcomePriceHistoryState,
    outcomeCashFlowState,
    snapshotDate,
    redactionEnabled,
    priceHistoryState,
    snapshotPriceHistoryRange,
  } = snapshotState;
  const hasSnapshotDate = Boolean(snapshotDate);
  const querySnapshotDate = searchParams.get('date');

  useEffect(() => {
    if (!isCompleteEmptyStateSnapshotDate(querySnapshotDate, maxSnapshotDate)) {
      return;
    }
    dispatchSnapshotState({
      type: 'setSnapshotDate',
      value: querySnapshotDate,
    });
  }, [maxSnapshotDate, querySnapshotDate]);

  useEffect(() => {
    if (!isAuthenticated && outcomesRevealed) {
      dispatchSnapshotState({ type: 'toggleOutcomes' });
    }
  }, [isAuthenticated, outcomesRevealed]);

  return (
    <Box>
      <StockSnapshotHeader
        displayTicker={displayTicker}
        snapshotData={snapshotData}
        snapshotDate={snapshotDate}
        maxSnapshotDate={maxSnapshotDate}
        status={status}
        redactionEnabled={redactionEnabled}
        outcomesRevealed={outcomesRevealed}
        isAuthenticated={isAuthenticated}
        onSnapshotDateChange={(nextDate) => dispatchSnapshotState({
          type: 'setSnapshotDate',
          value: nextDate,
        })}
        onToggleOutcomes={() => dispatchSnapshotState({ type: 'toggleOutcomes' })}
        onRequireLogin={() => {
          void loginWithRedirect(getLoginRedirectOptions());
        }}
        onRedactionChange={(checked) => dispatchSnapshotState({
          type: 'setRedactionEnabled',
          value: checked,
        })}
      />
      <SnapshotStatePanel
        status={status}
        error={error}
        hasDate={hasSnapshotDate}
        data={snapshotData}
        redactionEnabled={redactionEnabled}
        outcomesRevealed={outcomesRevealed}
        outcomeState={outcomeState}
        priceHistoryState={priceHistoryState}
        priceHistoryRange={snapshotPriceHistoryRange}
        onPriceHistoryRangeChange={(nextRange) => dispatchSnapshotState({
          type: 'setSnapshotPriceHistoryRange',
          value: nextRange,
        })}
        outcomePriceHistoryState={outcomePriceHistoryState}
        outcomeCashFlowState={outcomeCashFlowState}
        snapshotDate={snapshotDate}
        maxSnapshotDate={maxSnapshotDate}
        onSnapshotDateChange={(nextDate) => dispatchSnapshotState({
          type: 'setSnapshotDate',
          value: nextDate,
        })}
      />
    </Box>
  );
}

export default StockSnapshot;
